import { useEffect } from 'react';

/** How far, and how fast at most, a finger swipes a sheet down to close it. */
const MIN_DISTANCE = 56;
const MAX_DURATION = 700;

/** The sheets a swipe down closes: menus, selects and popovers on a touch screen, and sheets. */
export const SHEET_SELECTOR = [
  '[data-slot="dropdown-menu-content"]',
  '[data-slot="dropdown-menu-sub-content"]',
  '[data-slot="popover-content"]',
  '[data-slot="select-content"]',
  // A long press's context menu (a project's row, a favourite tile).
  '[data-slot="context-menu-content"]',
  '[data-slot="context-menu-sub-content"]',
  '[data-sheet]',
].join(', ');

/** Whether a swipe closes the sheet it started in: down, from its top (not scrolling it). */
export function closesSheet(
  swipe: { dx: number; dy: number; duration: number },
  scrolledToTop: boolean,
): boolean {
  return (
    scrolledToTop &&
    swipe.dy >= MIN_DISTANCE &&
    swipe.dy > 2 * Math.abs(swipe.dx) &&
    swipe.duration <= MAX_DURATION
  );
}

/** A swipe down on a sheet closes it (Radix menus and dialogs, and sheets, close on Escape). */
export function useSheetSwipeDown(): void {
  useEffect(() => {
    let start: { x: number; y: number; time: number; sheet: Element } | null = null;
    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      const sheet = event.target instanceof Element ? event.target.closest(SHEET_SELECTOR) : null;
      start =
        event.touches.length === 1 && touch !== undefined && sheet !== null
          ? { x: touch.clientX, y: touch.clientY, time: event.timeStamp, sheet }
          : null;
    };
    const onEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0];
      const began = start;
      start = null;
      if (began === null || touch === undefined) return;
      const list = began.sheet.querySelector('[data-radix-select-viewport]') ?? began.sheet;
      const swipe = {
        dx: touch.clientX - began.x,
        dy: touch.clientY - began.y,
        duration: event.timeStamp - began.time,
      };
      if (closesSheet(swipe, list.scrollTop <= 0))
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, []);
}
